import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DocumentList from './pages/DocumentList';
import DocumentDetail from './pages/DocumentDetail';
import DocumentCreate from './pages/DocumentCreate';
import RetrievalTest from './pages/RetrievalTest';
import DataManagement from './pages/DataManagement';
import ChatWidget from './pages/ChatWidget';
import Settings from './pages/Settings';
import Users from './pages/Users';
import Chats from './pages/Chats';
import Feedback from './pages/Feedback';
import ContentGaps from './pages/ContentGaps';
import WhatsApp from './pages/WhatsApp';
import FacultiesList from './pages/data/FacultiesList';
import DomainsList from './pages/data/DomainsList';
import DomainForm from './pages/data/DomainForm';
import ProgramsList from './pages/data/ProgramsList';
import ProgramForm from './pages/data/ProgramForm';
import ContactsList from './pages/data/ContactsList';
import ContactForm from './pages/data/ContactForm';
import DepartmentsList from './pages/data/DepartmentsList';
import DepartmentForm from './pages/data/DepartmentForm';
import AdmissionGradesList from './pages/data/AdmissionGradesList';
import AdmissionGradeForm from './pages/data/AdmissionGradeForm';
import { isSessionValid, logout } from './api/auth';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  // Validate the SESSION, not just token presence — an expired/garbage JWT
  // must never render the admin shell. Clear it so nothing downstream
  // half-trusts a dead token; the axios 401 interceptor stays as backstop.
  if (!isSessionValid()) {
    logout();
    return <Navigate to="/login" replace />;
  }
  return <Layout>{children}</Layout>;
}

function LoginRoute({ onLogin }: { onLogin: () => void }) {
  // Read the session when the route renders, not when App renders: a
  // client-side logout + navigate('/login') re-renders <Routes> but not App.
  return isSessionValid() ? <Navigate to="/" replace /> : <Login onLogin={onLogin} />;
}

function App() {
  // No cached auth flag: LoginRoute reads the session itself so a client-side
  // logout (Sign out, expiry) lands on /login instead of bouncing between
  // /login and / forever. The tick only forces a re-render after login.
  const [, setAuthTick] = useState(0);

  return (
    <Router>
      <Routes>
        <Route 
          path="/login" 
          element={<LoginRoute onLogin={() => setAuthTick((t) => t + 1)} />} 
        />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/chat-widget" element={<ProtectedRoute><ChatWidget /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><DocumentList /></ProtectedRoute>} />
        <Route path="/documents/:id" element={<ProtectedRoute><DocumentDetail /></ProtectedRoute>} />
        <Route path="/documents/create" element={<ProtectedRoute><DocumentCreate /></ProtectedRoute>} />
        <Route path="/retrieval-test" element={<ProtectedRoute><RetrievalTest /></ProtectedRoute>} />
        <Route path="/data" element={<ProtectedRoute><DataManagement /></ProtectedRoute>} />
        <Route path="/data/domains" element={<ProtectedRoute><DomainsList /></ProtectedRoute>} />
        <Route path="/data/domains/create" element={<ProtectedRoute><DomainForm /></ProtectedRoute>} />
        <Route path="/data/domains/:id/edit" element={<ProtectedRoute><DomainForm /></ProtectedRoute>} />
        <Route path="/data/faculties" element={<ProtectedRoute><FacultiesList /></ProtectedRoute>} />
        <Route path="/data/programs" element={<ProtectedRoute><ProgramsList /></ProtectedRoute>} />
        <Route path="/data/programs/create" element={<ProtectedRoute><ProgramForm /></ProtectedRoute>} />
        <Route path="/data/programs/:id/edit" element={<ProtectedRoute><ProgramForm /></ProtectedRoute>} />
        <Route path="/data/contacts" element={<ProtectedRoute><ContactsList /></ProtectedRoute>} />
        <Route path="/data/contacts/create" element={<ProtectedRoute><ContactForm /></ProtectedRoute>} />
        <Route path="/data/contacts/:id/edit" element={<ProtectedRoute><ContactForm /></ProtectedRoute>} />
        <Route path="/data/departments" element={<ProtectedRoute><DepartmentsList /></ProtectedRoute>} />
        <Route path="/data/departments/create" element={<ProtectedRoute><DepartmentForm /></ProtectedRoute>} />
        <Route path="/data/departments/:id/edit" element={<ProtectedRoute><DepartmentForm /></ProtectedRoute>} />
        <Route path="/data/admission-grades" element={<ProtectedRoute><AdmissionGradesList /></ProtectedRoute>} />
        <Route path="/data/admission-grades/create" element={<ProtectedRoute><AdmissionGradeForm /></ProtectedRoute>} />
        <Route path="/data/admission-grades/:id/edit" element={<ProtectedRoute><AdmissionGradeForm /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
        <Route path="/chats" element={<ProtectedRoute><Chats /></ProtectedRoute>} />
        <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
        <Route path="/content-gaps" element={<ProtectedRoute><ContentGaps /></ProtectedRoute>} />
        <Route path="/whatsapp" element={<ProtectedRoute><WhatsApp /></ProtectedRoute>} />
      </Routes>
    </Router>
  );
}

export default App;
